import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';

const KEY = 'h2h_url_promo_code';
const read = () => { try { return localStorage.getItem(KEY); } catch { return null; } };
const write = (v: string | null) => {
  try { if (v) localStorage.setItem(KEY, v); else localStorage.removeItem(KEY); } catch { /* ignore */ }
};

/**
 * ?code=XYZ: validated through the existing applyPromoCode (validate-promo-code)
 * and applied to single products only. Set carts never get it; the code is
 * re-applied once the cart holds single products again. Invalid codes are
 * dropped silently; manual entry keeps working.
 */
export const UrlPromoCode = () => {
  const { search } = useLocation();
  const { hydrated, items, promoCode, applyPromoCode } = useCart();
  const busy = useRef(false);
  const lastApplied = useRef<string | null>(null);

  useEffect(() => {
    const code = new URLSearchParams(search).get('code')?.trim().toUpperCase();
    if (code && /^[A-Z0-9_-]{2,40}$/.test(code)) write(code);
  }, [search]);

  const hasSet = items.some((i) => i.id === 'bundle');

  useEffect(() => {
    if (!hydrated || busy.current) return;
    const pending = read();
    if (!pending) return;
    if (promoCode) {
      if (promoCode.code !== pending) write(null); // another code was entered manually
      else lastApplied.current = pending;
      return;
    }
    if (hasSet) { lastApplied.current = null; return; } // never on set positions
    // Code was active and the user removed it while no set is in the cart.
    if (lastApplied.current === pending && !hasSet) { write(null); lastApplied.current = null; return; }
    busy.current = true;
    applyPromoCode(pending)
      .then((r) => { if (r.success) lastApplied.current = pending; else write(null); })
      .finally(() => { busy.current = false; });
  }, [hydrated, promoCode, hasSet, items.length, applyPromoCode, search]);

  return null;
};
